import React, { useState } from "react";
import { useLoaderData, useRevalidator } from "react-router-dom";

import useService from "../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../global/ApiRequestErrorHandler";
import ResponseModal from "../../../../global/ResponseModal";
import ProviderDetail from "../../provider-detail";
import ProviderSearch from "../../search-provider/ProviderSearch";
import AlternativeProvidersModal from "../AlternativeProvidersModal";

import AddCustomProvider from "./AddCustomProvider";

export default function AddProviderModal({ openModal, setOpenModal }) {
  const { _case } = useLoaderData();
  const { revalidate } = useRevalidator();
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState([]);
  const [assignedProvider, setAssignedProvider] = useState("");
  const [openCustomProv, setOpenCustomProv] = useState(false);
  const [loading, setLoading] = useState(false);
  const [providerDetail, setProviderDetail] = useState(null);
  const [showAlternativesModal, setShowAlternativesModal] = useState(false);
  const [alternativesData, setAlternativesData] = useState(null);
  const [loadingAlternatives, setLoadingAlternatives] = useState(false);
  const [providerData, setProviderData] = useState(null);

  const caseRepository = useService("repositories/case-repository");
  const providerRepository = useService("repositories/provider-repository");
  const providerClientStatusRepository = useService(
    "repositories/provider-client-status-repository"
  );

  const handleOpenSuccess = () => {
    setOpenCustomProv(false);
    setOpenSuccess(false);
    setOpenModal(false);
    revalidate();
  };

  // Checks for alternative providers before adding out-of-network provider
  const checkForAlternatives = async (providerId) => {
    if (!providerId) {
      return;
    }

    setLoadingAlternatives(true);
    try {
      const data = await caseRepository.searchForAlternatives(_case.id, { id: providerId });

      if (data && data.alternative_providers && data.alternative_providers.length > 0) {
        setAlternativesData(data);
        setProviderData({ providerId });
        setShowAlternativesModal(true);
      }
    } catch (error) {
      if (error.status === 404) {
        handleAddProCliStatus(providerId);
      } else {
        const errArr = ApiRequestErrorHandler(error.data);
        setErrorMessage(errArr);
        setOpenError(true);
      }
    } finally {
      setLoadingAlternatives(false);
    }
  };

  const handleAddNewProvider = async (justification, newProviderData) => {
    try {
      const response = await providerRepository.createRecord({
        ...newProviderData,
      });

      handleAddProCliStatus(response?.id, justification);
    } catch (error) {
      const errArr = ApiRequestErrorHandler(error);
      setErrorMessage(errArr);
      setOpenError(true);
    }
  };

  // Checks for alternatives based on provider types for new providers
  const checkForAlternativesWithTypes = async (providerTypes, newProviderData) => {
    if (!providerTypes) {
      return;
    }

    setLoadingAlternatives(true);
    try {
      const data = await caseRepository.searchForAlternatives(_case.id, {
        provider_types: providerTypes,
      });

      if (data && data.alternative_providers && data.alternative_providers.length > 0) {
        setAlternativesData(data);
        setProviderData(newProviderData);
        setShowAlternativesModal(true);
      }
    } catch (error) {
      if (error.status === 404) {
        handleAddNewProvider(null, newProviderData);
      } else {
        const errArr = ApiRequestErrorHandler(error.data);
        setErrorMessage(errArr);
        setOpenError(true);
      }
    } finally {
      setLoadingAlternatives(false);
    }
  };

  // Closes alternatives modal and clears data
  const handleCloseAlternativesModal = () => {
    setShowAlternativesModal(false);
    setAlternativesData(null);
  };

  // Confirms adding original provider after viewing alternatives
  const handleConfirmOriginalProvider = (justification) => {
    setShowAlternativesModal(false);
    if (providerData?.providerId) {
      handleAddProCliStatus(providerData.providerId, justification);
    } else {
      handleAddNewProvider(justification, providerData);
    }
  };

  // Selects an alternative provider from the modal
  const handleSelectAlternativeProvider = (providerId) => {
    setShowAlternativesModal(false);
    handleAddProCliStatus(providerId);
  };

  const handleAddProCliStatus = async (providerId, justification) => {
    setLoading(true);
    const assignNewProvider = {
      client: _case.client?.id,
      provider: providerId || assignedProvider,
      ...(justification ? { out_of_network_justification: justification } : {}),
    };

    try {
      const response = await providerClientStatusRepository.post(assignNewProvider);
      if (response) {
        setOpenSuccess(true);
        setLoading(false);
      }
    } catch (error) {
      const errArr = ApiRequestErrorHandler(error);
      setErrorMessage(errArr);
      setLoading(false);
      setOpenError(true);
    }
  };

  const handleOpenProvModal = () => {
    setOpenCustomProv(!openCustomProv);
  };

  return (
    <div>
      <div className="mt-4 flex w-[95%] flex-col justify-center">
        <ProviderSearch
          isModal={true}
          openModal={openModal}
          setCloseModal={() => setOpenModal(false)}
          caseInfo={_case?.client}
          handleOnClickCreateCustomProvider={handleOpenProvModal}
          handleOnClickAddProvider={checkForAlternatives}
        />

        <ResponseModal
          description="Successfully Added Provider to Client's Data"
          handleCloseFunc={handleOpenSuccess}
          openBool={openSuccess}
          setOpenBool={setOpenSuccess}
          title="Provider Added"
        />
        <ResponseModal
          title="Error Assigning Provider"
          isError={true}
          openBool={openError}
          setOpenBool={setOpenError}
          errorMessage={errorMessage}
        />
        <AddCustomProvider
          openCustomProv={openCustomProv}
          setOpenCustomProv={setOpenCustomProv}
          checkForAlternativesWithTypes={checkForAlternativesWithTypes}
        />
        {providerDetail && (
          <ProviderDetail
            open
            providerDetail={providerDetail}
            setOpen={() => setProviderDetail(null)}
          />
        )}
        <AlternativeProvidersModal
          open={showAlternativesModal}
          onClose={handleCloseAlternativesModal}
          onSelectAlternative={handleSelectAlternativeProvider}
          onConfirmOriginalProvider={handleConfirmOriginalProvider}
          alternativesData={alternativesData}
          isLoading={loadingAlternatives}
        />
      </div>
    </div>
  );
}
